import { useEffect, useState } from "react";

const MOBILE_MODAL_QUERY = "(max-width: 767px), (max-width: 991px) and (max-height: 575px) and (pointer: coarse)";

// Restore only properties owned by the lock, including their original priority.
const rememberStyles = (style, properties) => {
  const previous = properties.map((property) => [property, style.getPropertyValue(property), style.getPropertyPriority(property)]);
  return () => previous.forEach(([property, value, priority]) => {
    if (value) style.setProperty(property, value, priority);
    else style.removeProperty(property);
  });
};

export default function useModalViewport(open) {
  const [viewportStyle, setViewportStyle] = useState({});

  useEffect(() => {
    if (!open) return undefined;

    const viewport = window.visualViewport;
    let frame = null;
    const updateViewport = () => {
      frame = null;
      const height = `${viewport?.height || window.innerHeight}px`;
      const top = `${Math.max(0, viewport?.offsetTop || 0)}px`;
      setViewportStyle((previous) => (
        previous["--contact-viewport-height"] === height && previous["--contact-viewport-top"] === top
          ? previous
          : { "--contact-viewport-height": height, "--contact-viewport-top": top }
      ));
    };
    const scheduleUpdate = () => {
      if (frame === null) frame = window.requestAnimationFrame(updateViewport);
    };

    // Unlike dvh alone, the visual viewport accounts for the iOS keyboard.
    updateViewport();
    viewport?.addEventListener("resize", scheduleUpdate);
    viewport?.addEventListener("scroll", scheduleUpdate);
    window.addEventListener("resize", scheduleUpdate);

    let unlockBody;
    if (window.matchMedia(MOBILE_MODAL_QUERY).matches) {
      const bodyStyle = document.body.style;
      const x = window.scrollX;
      const y = window.scrollY;
      const restoreBody = rememberStyles(bodyStyle, ["position", "top", "left", "width", "overflow-y"]);

      // AntD locks overflow; fixed positioning additionally prevents iOS page panning.
      bodyStyle.setProperty("position", "fixed");
      bodyStyle.setProperty("top", `${-y}px`);
      bodyStyle.setProperty("left", `${-x}px`);
      bodyStyle.setProperty("width", "100%");
      bodyStyle.setProperty("overflow-y", "hidden");

      unlockBody = () => {
        const rootStyle = document.documentElement.style;
        const restoreScrollBehavior = rememberStyles(rootStyle, ["scroll-behavior"]);
        // Bootstrap enables smooth root scrolling; restoration must be immediate.
        rootStyle.setProperty("scroll-behavior", "auto", "important");
        restoreBody();
        window.scrollTo(x, y);
        restoreScrollBehavior();
      };
    }

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      viewport?.removeEventListener("resize", scheduleUpdate);
      viewport?.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      unlockBody?.();
    };
  }, [open]);

  return open ? viewportStyle : {};
}
